<!-- Responsibility: Implement scene current location behavior for the ui/components surface.
Scope: Owns ui/components concerns only; cross-surface orchestration is excluded. -->
<scene-current-location>
  <article
    if={ props.view?.visible }
    class="current-room-entry"
    data-expanded={ props.view?.expanded ? 'true' : 'false' }
    data-redundant={ props.view?.redundant ? 'true' : 'false' }
    role={ props.view?.interactive === false ? undefined : 'button' }
    tabindex={ props.view?.interactive === false ? undefined : '0' }
    aria-label={ `Focus current location details for ${props.view?.roomName || 'the current room'}` }
    onclick={ props.view?.interactive === false ? undefined : props.actions?.focusDetails }
    onfocusin={ props.view?.interactive === false ? undefined : props.actions?.expandDetails }
    onfocusout={ props.view?.interactive === false ? undefined : onFocusOut }
    onkeydown={ props.view?.interactive === false ? undefined : ((e) => props.actions?.onActivateKey?.(e, props.actions?.focusDetails)) }
  >
    <header>
      <span>Current location</span>
      <h3>{ props.view?.roomName }</h3>
    </header>
    <button
      if={ props.view?.hasVision }
      class="vision-floater"
      data-block="vision"
      style="background-image: url({ props.view.visionUrl })"
      onclick={ props.actions?.openVisionLightbox }
      onkeydown={ (e) => props.actions?.onActivateKey?.(e, props.actions?.openVisionLightbox) }
      aria-label="Open location image"
    ></button>
    <raw-html if={ props.view?.currentRoomDesc } class="scene-prose scene-room-prose" mode="prose" content={ props.view.currentRoomDesc }></raw-html>
    <p if={ props.view?.redundant } data-note="redundant">
      Location narrative already reflected in latest chronicle entry.
    </p>
    <section
      if={ props.view?.hasPresence }
      data-block="presence"
      onclick={ stopEvent }
      onkeydown={ stopKeyPropagation }
    >
      <header>
        <strong>In this location</strong>
        <small aria-hidden="true">Swipe ↔</small>
      </header>
      <ul if={ props.view?.showRoomLegend } data-block="legend" aria-label="Entity status color legend">
        <li each={ chip in props.view.roomLegendItems } data-kind={ chip.kind }>{ chip.label }</li>
      </ul>
      <div data-block="entities">
        <ul if={ props.view?.companions?.length } data-kind="companions">
          <li
            each={ c in props.view.companions }
          >
            <button
              type="button"
              data-e2e-open="companion"
              data-selected={ c.isSelected ? 'true' : 'false' }
              class="tooltip-wrap { c.isSelected ? 'selected' : '' }"
              data-tip-lock="off"
              onclick={ (e) => openCompanionChip(e, c) }
              aria-label={ c.ariaLabel || `Inspect companion ${c.name}` }
              onkeydown={ (e) => props.actions?.onActivateKey?.(e, () => props.actions?.openCompanionFromChip?.(c)) }
            >
              <span data-role="portrait">
                <img if={ c.portraitUrl } src={ c.portraitUrl } alt={ c.name }>
                { !c.portraitUrl ? c.name[0].toUpperCase() : '' }
              </span>
              <span data-role="summary">
                <strong>{ c.name.split(' ')[0] }</strong>
                <small data-vital={ vitalTone(c.vitals) }>{ vitalText(c.vitals) }</small>
                <small if={ c.activeEffectCount > 0 } data-role="effects">FX { c.activeEffectCount }</small>
              </span>

            </button>
          </li>
        </ul>
        <ul if={ props.view?.roomNpcs?.length } data-kind="npcs">
          <li each={ npc in props.view.roomNpcs }><npc-card npc={ npc } compact={ true } showTooltip={ false }></npc-card></li>
        </ul>
        <ul if={ props.view?.roomItems?.length } data-kind="items">
          <li each={ item in props.view.roomItems }><item-card name={ item } location="room" compact={ true } showTooltip={ false }></item-card></li>
        </ul>
        <ul if={ props.view?.roomFlags?.length || props.view?.roomDormantFlags?.length } data-kind="flags">
          <li each={ flag in props.view.roomFlags }>
            <article data-state="active">
              <strong>Active</strong>
              <span>{ flag }</span>
            </article>
          </li>
          <li each={ flag in props.view.roomDormantFlags }>
            <article data-state="dormant">
              <strong>Dormant</strong>
              <span>{ flag }</span>
            </article>
          </li>
        </ul>
      </div>
    </section>
    <footer>
      <span>{ props.view?.interactive === false ? 'Scene focus' : '▾' }</span>
      <span>{ props.view?.interactive === false ? 'Current location stays fully visible while the action deck folds below.' : (props.view?.redundant ? 'Hover or focus for full location details' : 'Hover or focus to enlarge location details') }</span>
    </footer>
  </article>

  <script>
    import RawHtml from '../components/raw-html.riot'
    import NpcCard from './npc-card.riot'
    import ItemCard from './item-card.riot'
    import { describeVital } from '../../engine/vitals'

    export default {
      components: { RawHtml, NpcCard, ItemCard },

      stopEvent(e) {
        e?.stopPropagation?.()
      },

      stopKeyPropagation(e) {
        if (e?.key === 'Enter' || e?.key === ' ') e.stopPropagation()
      },

      openCompanionChip(e, companion) {
        e?.stopPropagation?.()
        this.props.actions?.openCompanionFromChip?.(companion)
      },

      onFocusOut(event) {
        const nextTarget = event?.relatedTarget
        const host = this.$('article')
        if (nextTarget instanceof Element && host?.contains?.(nextTarget)) return
        this.props.actions?.collapseDetails?.()
      },
      vitalText(vitals) {
        return describeVital(vitals ?? {}, 'vitality').label
      },

      vitalTone(vitals) {
        return describeVital(vitals ?? {}, 'vitality').tone
      },
    }
  </script>
</scene-current-location>
